import type { HeardAboutUs } from "@repo/vihangam-yog-shared";

import type { BookingConfig } from "./booking-config";

/** The shared VY backend Worker; the same one vyaustralia.org books through. */
const PRODUCTION_API = "https://vy-australia-backend.kishek12.workers.dev/trpc";

/** In development Vite proxies /trpc to the Worker, so a local port never needs a CORS entry. */
const API_BASE = import.meta.env.DEV ? "/trpc" : PRODUCTION_API;

const REQUEST_TIMEOUT_MS = 20_000;

export type Fetcher = (url: string, init: RequestInit) => Promise<Response>;

const defaultFetcher: Fetcher = (url, init) => fetch(url, init);

/** Live state of one tier, as `eventTierAvailability` reports it. */
export interface TierAvailability {
  tierId: string;
  soldOut: boolean;
  remaining: number | null;
}

/** What the visitor chose; ids and quantities only. The Worker prices every line from the catalog. */
export interface CheckoutSelection {
  eventSlug: string;
  groupSize: number;
  tierId: string;
  addOns: { id: string; quantity: number }[];
  donationCents: number;
}

export interface Contact {
  name: string;
  email: string;
  phone?: string;
  heardAboutUs: HeardAboutUs;
}

export interface CheckoutRequest extends CheckoutSelection, Contact {}

export interface InterestRequest extends Contact {
  eventSlug: string;
}

export interface CheckoutSession {
  url: string;
}

export interface InterestReceipt {
  ok: true;
  confirmationRef: string;
}

export interface Basket {
  /** Cents, for display only; the Worker recomputes it. */
  total: number;
  input: CheckoutSelection;
}

/** The values a visitor can set on the form, as the controls hold them. */
export interface SelectionValues {
  groupSize: string;
  tierId: string;
  donation: string;
  addOns: readonly { id: string; quantity: string }[];
}

/** A message safe to show the visitor as it is. */
export class BookingError extends Error {
  readonly timedOut: boolean;

  constructor(message: string, timedOut = false) {
    super(message);
    this.name = "BookingError";
    this.timedOut = timedOut;
  }
}

const MESSAGES = {
  timeout:
    "The request timed out. No payment has been taken by this form. Please try again.",
  offline:
    "We could not reach the booking service. Please check your connection and try again.",
  unavailable: "Booking is unavailable at the moment. Please try again.",
  unexpected:
    "The booking service returned an unexpected response. Please try again.",
  tooSmall: "Choose a Kund with room for everyone in your group.",
  unavailableTier: "That option is unavailable. Please choose another Kund.",
  paymentLink:
    "The payment link could not be verified. Please contact Vihangam Yog Australia.",
} as const;

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- the decoder is the parse boundary for the tRPC envelope's `result.data`
export type Decoder<T> = (value: unknown) => value is T;

interface RpcCall<I> {
  procedure: string;
  input: I;
  mutation: boolean;
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: this IS the parse boundary for the tRPC success envelope
function isEnvelope(value: unknown): value is { result: { data: unknown } } {
  return (
    typeof value === "object" &&
    value !== null &&
    "result" in value &&
    typeof value.result === "object" &&
    value.result !== null &&
    "data" in value.result
  );
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: this IS the parse boundary for the tRPC error envelope
function isRefusal(value: unknown): value is { error: { message: string } } {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof value.error === "object" &&
    value.error !== null &&
    "message" in value.error &&
    typeof value.error.message === "string"
  );
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- a caught error: fetch rejects with a DOMException on abort
function isTimeout(error: unknown): boolean {
  return error instanceof DOMException && error.name === "TimeoutError";
}

/**
 * One tRPC-over-HTTP call against the shared Worker. Queries go as GET with
 * the input in the query string; mutations POST plain JSON. Whatever comes
 * back is decoded by the caller's predicate before it is trusted.
 */
// fallow-ignore-next-line unused-export -- exercised directly by its unit test
export async function rpc<I, T>(
  request: RpcCall<I>,
  decode: Decoder<T>,
  fetcher: Fetcher = defaultFetcher,
): Promise<T> {
  const query = request.mutation
    ? ""
    : `?input=${encodeURIComponent(JSON.stringify(request.input))}`;
  const init: RequestInit = {
    method: request.mutation ? "POST" : "GET",
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  };
  if (request.mutation) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(request.input);
  }
  let response: Response;
  try {
    response = await fetcher(`${API_BASE}/${request.procedure}${query}`, init);
  } catch (error) {
    if (isTimeout(error)) {
      throw new BookingError(MESSAGES.timeout, true);
    }
    throw new BookingError(MESSAGES.offline);
  }
  const body: unknown = await response.json().catch(() => null);
  if (isRefusal(body)) {
    throw new BookingError(body.error.message);
  }
  if (!response.ok) {
    throw new BookingError(MESSAGES.unavailable);
  }
  if (!isEnvelope(body) || !decode(body.result.data)) {
    throw new BookingError(MESSAGES.unexpected);
  }
  return body.result.data;
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: parse boundary for one row of eventTierAvailability
function isTierAvailability(value: unknown): value is TierAvailability {
  return (
    typeof value === "object" &&
    value !== null &&
    "tierId" in value &&
    typeof value.tierId === "string" &&
    "soldOut" in value &&
    typeof value.soldOut === "boolean" &&
    "remaining" in value &&
    (value.remaining === null || typeof value.remaining === "number")
  );
}

function isTierAvailabilityList(
  // oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: parse boundary for eventTierAvailability's data
  value: unknown,
): value is TierAvailability[] {
  return Array.isArray(value) && value.every(isTierAvailability);
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: parse boundary for createPaidBookingCheckout's data
function isCheckoutSession(value: unknown): value is CheckoutSession {
  return (
    typeof value === "object" &&
    value !== null &&
    "url" in value &&
    typeof value.url === "string"
  );
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: parse boundary for registerEventInterest's data
function isInterestReceipt(value: unknown): value is InterestReceipt {
  return (
    typeof value === "object" &&
    value !== null &&
    "ok" in value &&
    value.ok === true &&
    "confirmationRef" in value &&
    typeof value.confirmationRef === "string"
  );
}

/** Read-only: what is still open right now. */
export function fetchAvailability(
  eventSlug: string,
  fetcher?: Fetcher,
): Promise<TierAvailability[]> {
  return rpc(
    {
      procedure: "eventTierAvailability",
      input: { eventSlug },
      mutation: false,
    },
    isTierAvailabilityList,
    fetcher,
  );
}

/** Opens a Stripe Checkout session; the booking is only recorded once Stripe confirms payment. */
export function startCheckout(
  input: CheckoutRequest,
  fetcher?: Fetcher,
): Promise<CheckoutSession> {
  return rpc(
    { procedure: "createPaidBookingCheckout", input, mutation: true },
    isCheckoutSession,
    fetcher,
  );
}

/** Coming alone and preferring to talk first: no payment, the team pairs them into a Kund. */
export function registerInterest(
  input: InterestRequest,
  fetcher?: Fetcher,
): Promise<InterestReceipt> {
  return rpc(
    { procedure: "registerEventInterest", input, mutation: true },
    isInterestReceipt,
    fetcher,
  );
}

const DEFAULT_ADD_ON_MAX = 10;

/**
 * Turns the form's values into a priced basket, failing closed on anything
 * the Worker would refuse: a tier too small for the group, a tier the live
 * availability says is closed, a donation outside the guard rails, or an
 * add-on quantity that is not a whole number within its limit.
 */
export function selection(
  config: BookingConfig,
  values: SelectionValues,
  availability: readonly TierAvailability[] | undefined,
): Basket {
  const groupSize = Number(values.groupSize);
  const tier = config.event.price.tiers.find((t) => t.id === values.tierId);
  if (
    !tier ||
    !Number.isInteger(groupSize) ||
    groupSize < 1 ||
    groupSize > tier.seats
  ) {
    throw new BookingError(MESSAGES.tooSmall);
  }
  const live = availability?.find((a) => a.tierId === tier.id);
  if (tier.soldOut || !live || live.soldOut) {
    throw new BookingError(MESSAGES.unavailableTier);
  }
  const { minCents, maxCents } = config.donation;
  const donationCents = Math.round(Number(values.donation || "0") * 100);
  if (
    !Number.isFinite(donationCents) ||
    donationCents < 0 ||
    (donationCents > 0 && donationCents < minCents) ||
    donationCents > maxCents
  ) {
    throw new BookingError(
      `An optional donation must be between $${minCents / 100} and $${maxCents / 100}.`,
    );
  }
  const catalogAddOns = config.event.price.addOns ?? [];
  let addOnCents = 0;
  const addOns: CheckoutSelection["addOns"] = [];
  for (const addOn of catalogAddOns) {
    const raw = values.addOns.find((a) => a.id === addOn.id)?.quantity ?? "0";
    const quantity = Number(raw || "0");
    const max = addOn.maxQuantity ?? DEFAULT_ADD_ON_MAX;
    if (!Number.isInteger(quantity) || quantity < 0 || quantity > max) {
      throw new BookingError(
        `Please choose a whole number of ${addOn.label}, from 0 to ${max}.`,
      );
    }
    if (quantity > 0) {
      addOns.push({ id: addOn.id, quantity });
      addOnCents += quantity * addOn.amountCents;
    }
  }
  return {
    total: tier.amountCents + donationCents + addOnCents,
    input: {
      eventSlug: config.event.slug,
      groupSize,
      tierId: tier.id,
      addOns,
      donationCents,
    },
  };
}

/** Only ever send the visitor to Stripe's own hosted checkout. */
export function checkoutUrl(value: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new BookingError(MESSAGES.paymentLink);
  }
  if (url.protocol !== "https:" || url.hostname !== "checkout.stripe.com") {
    throw new BookingError(MESSAGES.paymentLink);
  }
  return url.href;
}
